import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  setDoc,
  deleteDoc,
  addDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import './RecipesList.css';
import Drawer from './Drawer';
import PageHeader from './PageHeader';

function RecipesList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(
    location.state?.filterFavorites === true
  );


  // État pour suivre l'authentification
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Gestion des recettes sélectionnées pour courses avec compteurs
  const [selectedForShopping, setSelectedForShopping] = useState({});

  // État pour les favoris
  const [favorites, setFavorites] = useState({});

  // État pour le drawer de catégories
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);

  // État pour le drawer de planning
  const [planningDrawerOpen, setPlanningDrawerOpen] = useState(false);
  const [selectedRecipeForPlanning, setSelectedRecipeForPlanning] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState('');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [weekDates, setWeekDates] = useState([]);
  const [savingPlanning, setSavingPlanning] = useState(false);
  const [planningSuccess, setPlanningSuccess] = useState(false);


  // Fonction pour charger les favoris
  const loadFavorites = async currentUser => {
    if (!currentUser) return;

    try {
      const favoritesSnapshot = await getDocs(
        collection(db, `recettesFavorites/${currentUser.uid}/recettes`)
      );

      const favoritesData = {};
      favoritesSnapshot.forEach(doc => {
        favoritesData[doc.data().idRecette] = true;
      });

      setFavorites(favoritesData);
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
    }
  };

  // Fonction pour basculer un favori
  const toggleFavorite = async (recipeId, event) => {
    event.stopPropagation();

    if (!user) return;

    try {
      const isFavorite = favorites[recipeId];
      const favoriteDocRef = doc(
        db,
        `recettesFavorites/${user.uid}/recettes`,
        recipeId
      );

      if (isFavorite) {
        await deleteDoc(favoriteDocRef);
        setFavorites(prev => {
          const updated = { ...prev };
          delete updated[recipeId];
          return updated;
        });
      } else {
        await setDoc(favoriteDocRef, {
          idRecette: recipeId,
          dateAjout: new Date(),
        });
        setFavorites(prev => ({
          ...prev,
          [recipeId]: true,
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la gestion des favoris:', error);
      setError('Erreur lors de la sauvegarde du favori');
    }
  };

  // Charger les recettes depuis Firestore (utilisateur + par défaut)
  const loadRecipes = async currentUser => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const userRecipesSnapshot = await getDocs(
        query(
          collection(db, `recettesUtilisateur/${currentUser.uid}/recettes`),
          orderBy('dateCreation', 'desc')
        )
      );

      const userRecipes = userRecipesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setRecipes(userRecipes);
    } catch (err) {
      console.error('Erreur lors du chargement des recettes:', err);
      setError('Erreur lors du chargement des recettes');
    } finally {
      setLoading(false);
    }
  };

  // Charger les catégories depuis Firestore
  const loadCategories = async currentUser => {
    if (!currentUser) return;

    try {
      const categoriesSnapshot = await getDocs(
        collection(db, `categoriesUtilisateur/${currentUser.uid}/categories`)
      );
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  // Effet pour gérer l'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
      setAuthLoading(false);

      if (currentUser) {
        Promise.all([
          loadRecipes(currentUser),
          loadCategories(currentUser),
          loadFavorites(currentUser),
        ]);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Basculer la sélection d'une catégorie (dans le drawer)
  const toggleCategory = catId => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  // Fonction pour obtenir une catégorie par ID
  const getCategoryById = categoryId => {
    return categories.find(cat => cat.id === categoryId);
  };

  // Fonction pour calculer le temps total
  const getTotalTime = recipe => {
    const prep = recipe.tempsPreparation || 0;
    const cook = recipe.tempsCuisson || 0;
    return prep + cook;
  };

  // Calcul des dates de la semaine
  const calculateWeek = (offset = 0) => {
    const today = new Date();
    const startOfWeek = new Date(today);

    // Calculer le début de la semaine (lundi)
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    // Ajouter l'offset de semaine
    startOfWeek.setDate(startOfWeek.getDate() + (offset * 7));

    // Générer les 7 jours
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // Initialiser les dates de la semaine
  useEffect(() => {
    setWeekDates(calculateWeek(currentWeekOffset));
  }, [currentWeekOffset]);

  // Obtenir le titre de la semaine
  const getWeekTitle = () => {
    if (currentWeekOffset === 0) return 'Cette semaine';
    if (currentWeekOffset === 1) return 'Semaine prochaine';
    if (currentWeekOffset === -1) return 'Semaine dernière';
    if (currentWeekOffset > 1) return `Dans ${currentWeekOffset} semaines`;
    return `Il y a ${Math.abs(currentWeekOffset)} semaines`;
  };

  // Navigation entre les semaines
  const changeWeek = (direction) => {
    setCurrentWeekOffset(prev => prev + direction);
    setSelectedDay(null);
    setSelectedMealType('');
  };

  // Vérifier si on peut naviguer vers le passé
  const canGoPrevious = () => {
    const today = new Date();
    const firstDayOfWeek = calculateWeek(currentWeekOffset - 1)[0];
    return firstDayOfWeek >= today.setHours(0, 0, 0, 0);
  };

  // Ouvrir le drawer de planning
  const openPlanningDrawer = (recipe, event) => {
    event.stopPropagation();
    setSelectedRecipeForPlanning(recipe);
    setPlanningDrawerOpen(true);
    setPlanningSuccess(false);
    setCurrentWeekOffset(0);
    setSelectedDay(null);
    setSelectedMealType('');
  };

  // Fermer le drawer de planning
  const closePlanningDrawer = () => {
    setPlanningDrawerOpen(false);
    setTimeout(() => {
      setSelectedRecipeForPlanning(null);
      setSelectedDay(null);
      setSelectedMealType('');
      setPlanningSuccess(false);
      setCurrentWeekOffset(0);
    }, 300);
  };

  // Formater la date pour l'affichage
  const formatDateDisplay = (date) => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Vérifier si une date est aujourd'hui
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Sauvegarder le planning dans Firestore
  const savePlanningToFirestore = async () => {
    if (!user || !selectedRecipeForPlanning || !selectedDay || !selectedMealType) {
      return;
    }

    // Mapping vers les IDs utilisés par MealPlanner
    const mealTypeMap = {
      'breakfast': 'breakfast',
      'lunch': 'lunch',
      'dinner': 'dinner',
    };
    const mealTypeKey = mealTypeMap[selectedMealType] || selectedMealType;

    try {
      setSavingPlanning(true);

      const planningCollection = collection(db, 'planningRepas', user.uid, 'plans');

      // Chercher si un plan existe déjà pour ce jour
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);

      const q = query(
        planningCollection,
        where('date', '>=', Timestamp.fromDate(dayStart)),
        where('date', '<=', Timestamp.fromDate(dayEnd))
      );
      const existing = await getDocs(q);

      if (!existing.empty) {
        // Fusionner avec le plan existant
        const planRef = doc(planningCollection, existing.docs[0].id);
        const existingData = existing.docs[0].data();
        await updateDoc(planRef, {
          repas: {
            ...(existingData.repas || {}),
            [mealTypeKey]: {
              idRecette: selectedRecipeForPlanning.id,
              nombrePersonnes: selectedRecipeForPlanning.nombrePersonnes || 4,
            },
          },
          dateMiseAJour: serverTimestamp(),
        });
      } else {
        // Créer un nouveau plan
        await addDoc(planningCollection, {
          date: Timestamp.fromDate(dayStart),
          repas: {
            [mealTypeKey]: {
              idRecette: selectedRecipeForPlanning.id,
              nombrePersonnes: selectedRecipeForPlanning.nombrePersonnes || 4,
            },
          },
          notes: '',
          dateCreation: serverTimestamp(),
          dateMiseAJour: serverTimestamp(),
        });
      }

      setPlanningSuccess(true);
      setTimeout(() => {
        closePlanningDrawer();
      }, 1500);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du planning:', error);
      setError('Erreur lors de l\'ajout au planning');
    } finally {
      setSavingPlanning(false);
    }
  };

  // Gestion des recettes sélectionnées pour courses (toggle : ajouter ou retirer)
  const toggleRecipeForShopping = (recipeId, event) => {
    event.stopPropagation();
    setSelectedForShopping(prev => {
      if (prev[recipeId]) {
        const updated = { ...prev };
        delete updated[recipeId];
        return updated;
      }
      return { ...prev, [recipeId]: 1 };
    });
  };

  // Fonction utilitaire pour le compteur de courses
  const getShoppingCount = recipeId => selectedForShopping[recipeId] || 0;

  // Navigation vers les courses avec les recettes sélectionnées
  const goToShopping = () => {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 7);

    navigate('/shopping-list', {
      state: {
        selectedRecipes: selectedForShopping,
        fromRecipes: true,
        dateRange: {
          startDate: today,
          endDate: endDate,
        },
      },
    });
  };

  // Navigation vers les détails d'une recette
  const navigateToRecipe = recipeId => {
    navigate(`/recipe/${recipeId}`);
  };

  // Filtrage des recettes (même logique que MealPlanner)
  const filteredRecipes = recipes.filter(recipe => {
    // 1. Vérification du terme de recherche textuel
    const matchesSearchTerm =
      searchTerm.trim() === '' ||
      recipe.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (recipe.description &&
        recipe.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // 2. Vérification des catégories sélectionnées (opérateur ET)
    const matchesCategories =
      selectedCategories.length === 0 ||
      selectedCategories.every(
        categoryId =>
          recipe.categories && recipe.categories.includes(categoryId)
      );

    // 3. Filtre favoris
    const matchesFavorites = !showFavoritesOnly || favorites[recipe.id];

    return matchesSearchTerm && matchesCategories && matchesFavorites;
  });

  // Gérer le chargement de l'authentification
  if (authLoading) {
    return <div className="loading">Chargement...</div>;
  }

  // Gérer le chargement des données
  if (loading) {
    return <div className="loading">Chargement des recettes...</div>;
  }

  // Gérer les erreurs
  if (error) {
    return (
      <div className="recipes-list">
        <div className="recipes-list-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#f44336', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="action-button"
            >
              Recharger la page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recipes-list">
      {/* Version marker: v2.0.0 - Categories fix */}
      <PageHeader
        title="Mes Recettes"
        backTo="/"
        backAriaLabel="Retour à l'accueil"
        sticky={true}
        actions={
          <>
            <button
              className="add-recipe-header-btn add-recipe-header-btn--outline"
              onClick={() => navigate('/catalogue')}
              title="Catalogue de recettes"
              style={{ marginRight: '8px' }}
            >
              📚 Catalogue
            </button>
            <button
              className="add-recipe-header-btn"
              onClick={() => navigate('/add-recipe')}
              title="Ajouter une recette"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Recette
            </button>
          </>
        }
      >
        {/* === SEARCH + CATEGORY CHIPS === */}
        <div className="search-filter-section">
          <div className="search-input-wrapper">
            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              className="recipe-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher une recette..."
            />
            {searchTerm && (
              <button className="search-clear-btn" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>

          <div className="filter-row">
            {categories.length > 0 && (
              <button
                className={`btn-filter ${selectedCategories.length > 0 ? 'btn-filter-active' : ''}`}
                onClick={() => setCategoryDrawerOpen(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                {selectedCategories.length === 0 ? 'Catégories' : 'Filtrer'}
                {selectedCategories.length > 0 && (
                  <span className="filter-count">{selectedCategories.length}</span>
                )}
              </button>
            )}

            <button
              className={`chip chip-fav ${showFavoritesOnly ? 'chip-fav-active' : ''}`}
              onClick={() => setShowFavoritesOnly(v => !v)}
            >
              ★ Favoris
            </button>

            {selectedCategories.length > 0 && (
              <div className="active-chips-strip">
                {selectedCategories.map(catId => {
                  const cat = getCategoryById(catId);
                  return cat ? (
                    <button
                      key={catId}
                      className="chip-active-remove"
                      style={{ backgroundColor: cat.couleur }}
                      onClick={() => setSelectedCategories(prev => prev.filter(id => id !== catId))}
                    >
                      {cat.nom} <span aria-hidden="true">×</span>
                    </button>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>
      </PageHeader>

      {/* === MAIN CONTENT === */}
      <div className="recipes-list-container">
        {/* === SECTION HEADER === */}
        <div className="section-header">
          <h2 className="section-title">
            {selectedCategories.length === 0
              ? 'Toutes les recettes'
              : `Recettes ${categories
                  .filter(c => selectedCategories.includes(c.id))
                  .map(c => c.nom)
                  .join(', ')}`}
          </h2>
        </div>

        {/* === RESULTS HEADER (Bouton courses) === */}
        <div className="results-header">
          <div className="action-buttons">
            <button
              className={`shopping-button ${
                Object.keys(selectedForShopping).length > 0 ? 'active' : ''
              }`}
              onClick={goToShopping}
              disabled={Object.keys(selectedForShopping).length === 0}
            >
              🛒 Courses ({Object.keys(selectedForShopping).length})
              {Object.keys(selectedForShopping).length > 0 && (
                <span className="counter">
                  {Object.values(selectedForShopping).reduce(
                    (a, b) => a + b,
                    0
                  )}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* === AFFICHAGE DES RECETTES === */}
        {filteredRecipes.length === 0 ? (
          <div className="empty-recipes-message">
            {recipes.length === 0 ? (
              <>
                <div className="empty-state-illustration">
                  <svg width="96" height="96" viewBox="0 0 96 96" fill="none" aria-hidden="true">
                    <rect x="14" y="18" width="60" height="60" rx="5" style={{fill: 'var(--bg-secondary)', stroke: 'var(--border-color)'}} strokeWidth="1.5"/>
                    <rect x="14" y="18" width="9" height="60" rx="5" fill="#ff7043" opacity="0.2"/>
                    <line x1="33" y1="40" x2="66" y2="40" style={{stroke: 'var(--border-color)'}} strokeWidth="2" strokeLinecap="round"/>
                    <line x1="33" y1="52" x2="66" y2="52" style={{stroke: 'var(--border-color)'}} strokeWidth="2" strokeLinecap="round"/>
                    <line x1="33" y1="64" x2="54" y2="64" style={{stroke: 'var(--border-color)'}} strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="74" cy="26" r="16" style={{fill: 'var(--bg-paper)', stroke: '#ff7043'}} strokeWidth="1.5"/>
                    <line x1="74" y1="19" x2="74" y2="33" stroke="#ff7043" strokeWidth="2.5" strokeLinecap="round"/>
                    <line x1="67" y1="26" x2="81" y2="26" stroke="#ff7043" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="empty-state-title">Votre livre de recettes est vide</h3>
                <p className="empty-state-subtitle">
                  Ajoutez votre première recette pour commencer à planifier vos repas et générer vos listes de courses.
                </p>
                <button onClick={() => navigate('/add-recipe')} className="empty-state-cta">
                  Créer ma première recette
                </button>
              </>
            ) : (
              <>
                <p>Aucune recette ne correspond à vos critères de recherche.</p>
                <button
                  onClick={() => { setSearchTerm(''); setSelectedCategories([]); setShowFavoritesOnly(false); }}
                  className="action-button"
                >
                  Réinitialiser les filtres
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="recipes-list-view">
              {filteredRecipes.map(recipe => (
                <div
                  key={recipe.id}
                  className="recipe-list-item"
                  onClick={() => navigateToRecipe(recipe.id)}
                >
                  <div className="list-item-main">
                    <div className="list-item-info">
                      <div className="list-item-header">
                        <h3 className="list-item-title">{recipe.nom}</h3>
                        {recipe.note > 0 && (
                          <div className="list-item-rating">
                            {[1, 2, 3, 4, 5].map(s => (
                              <span key={s} style={{ color: s <= recipe.note ? '#f59e0b' : 'var(--border-color)' }}>★</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="list-item-inline">
                        <div className="list-item-meta">
                          <div className="list-item-meta-item">
                            <span>👥 {recipe.nombrePersonnes || 4} pers.</span>
                          </div>
                          <div className="list-item-meta-item">
                            <span>⏱️ {getTotalTime(recipe)} min</span>
                          </div>
                        </div>
                        <div className="list-item-tags">
                          {recipe.categories && Array.isArray(recipe.categories) &&
                            recipe.categories.slice(0, 3).map((catId, idx) => {
                              const category = getCategoryById(catId);
                              if (category) {
                                return (
                                  <span
                                    key={category.id}
                                    className="list-tag"
                                    style={{
                                      backgroundColor: `${category.couleur}20`,
                                      color: category.couleur,
                                    }}
                                  >
                                    {category.nom}
                                  </span>
                                );
                              }
                              return null;
                            })
                          }
                        </div>
                      </div>
                    </div>

                    <div className="list-item-actions">
                      {/* Bouton favori */}
                      <button
                        className={`list-action-btn ${
                          favorites[recipe.id] ? 'favorite-active' : ''
                        }`}
                        onClick={e => toggleFavorite(recipe.id, e)}
                        title={
                          favorites[recipe.id]
                            ? 'Retirer des favoris'
                            : 'Ajouter aux favoris'
                        }
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24"
                          fill={favorites[recipe.id] ? 'currentColor' : 'none'}
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          aria-hidden="true">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      </button>

                      {/* Bouton courses */}
                      <button
                        className={`list-action-btn ${
                          getShoppingCount(recipe.id) > 0
                            ? 'shopping-active'
                            : ''
                        }`}
                        onClick={e => toggleRecipeForShopping(recipe.id, e)}
                        title="Courses"
                      >
                        🛒
                        {getShoppingCount(recipe.id) > 0 && (
                          <span className="quick-btn-count">
                            {getShoppingCount(recipe.id)}
                          </span>
                        )}
                      </button>

                      {/* Bouton planning */}
                      <button
                        className="list-action-btn"
                        onClick={e => openPlanningDrawer(recipe, e)}
                        title="Ajouter au planning"
                      >
                        🗓️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* === FAB PILL - Ajouter recette (mobile uniquement) === */}
      <button
        className="add-recipe-fab"
        style={Object.keys(selectedForShopping).length > 0 ? { bottom: '90px' } : undefined}
        onClick={() => navigate('/add-recipe')}
        title="Ajouter une recette"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Recette
      </button>

      {/* === FOOTER FIXE - Bouton courses (mobile uniquement) === */}
      {Object.keys(selectedForShopping).length > 0 && (
        <div className="recipes-list-fixed-footer">
          <div className="generate-shopping-action">
            <button className="shopping-footer-button" onClick={goToShopping}>
              🛒 Générer ma liste de courses (
              {Object.values(selectedForShopping).reduce((a, b) => a + b, 0)})
            </button>
          </div>
        </div>
      )}

      {/* === DRAWER CATÉGORIES === */}
      <Drawer
        isOpen={categoryDrawerOpen}
        onClose={() => setCategoryDrawerOpen(false)}
        title="Filtrer par catégorie"
      >
        <div className="categories-drawer-content">
          <div className="categories-list">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-item ${selectedCategories.includes(cat.id) ? 'active' : ''}`}
                onClick={() => toggleCategory(cat.id)}
              >
                <div className="category-color" style={{ backgroundColor: cat.couleur }}></div>
                <span className="category-name">{cat.nom}</span>
                {selectedCategories.includes(cat.id) && <span className="category-check">✓</span>}
              </button>
            ))}
          </div>
          <div className="categories-drawer-actions">
            <button
              className="categories-clear-button"
              onClick={() => setSelectedCategories([])}
              disabled={selectedCategories.length === 0}
            >
              Effacer
            </button>
            <button
              className="categories-apply-button"
              onClick={() => setCategoryDrawerOpen(false)}
            >
              {selectedCategories.length === 0 ? 'Toutes les recettes' : `Appliquer (${selectedCategories.length})`}
            </button>
          </div>
        </div>
      </Drawer>

      {/* === DRAWER PLANNING === */}
      <Drawer
        isOpen={planningDrawerOpen}
        onClose={closePlanningDrawer}
        title={selectedRecipeForPlanning ? `Ajouter "${selectedRecipeForPlanning.nom}" au planning` : 'Ajouter au planning'}
        className="planning-drawer"
      >
        {planningSuccess ? (
          <div className="planning-success-message">
            <div className="success-icon">✓</div>
            <p className="success-text">Recette ajoutée au planning avec succès !</p>
          </div>
        ) : (
          <>
            {/* Navigation entre les semaines */}
            <div className="week-navigator">
              <button
                className="week-nav-btn"
                onClick={() => changeWeek(-1)}
                disabled={!canGoPrevious()}
                title="Semaine précédente"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="week-title">{getWeekTitle()}</span>
              <button
                className="week-nav-btn"
                onClick={() => changeWeek(1)}
                title="Semaine suivante"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>

            {/* Sélecteur de jour */}
            <div className="planning-section">
              <h3 className="planning-section-title">Choisir un jour</h3>
              <div className="day-selector">
                {weekDates.map((date, index) => (
                  <button
                    key={index}
                    className={`day-card ${selectedDay && selectedDay.toDateString() === date.toDateString() ? 'selected' : ''} ${isToday(date) ? 'today' : ''}`}
                    onClick={() => setSelectedDay(date)}
                  >
                    <div className="day-name">{formatDateDisplay(date).split(' ')[0]}</div>
                    <div className="day-date">{date.getDate()}</div>
                    {isToday(date) && <div className="today-indicator">Aujourd'hui</div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Sélecteur de type de repas */}
            <div className="planning-section">
              <h3 className="planning-section-title">Type de repas</h3>
              <div className="meal-selector">
                <button
                  className={`meal-card ${selectedMealType === 'breakfast' ? 'selected' : ''}`}
                  onClick={() => setSelectedMealType('breakfast')}
                >
                  <div className="meal-icon">🌅</div>
                  <div className="meal-name">Petit-déjeuner</div>
                </button>
                <button
                  className={`meal-card ${selectedMealType === 'lunch' ? 'selected' : ''}`}
                  onClick={() => setSelectedMealType('lunch')}
                >
                  <div className="meal-icon">☀️</div>
                  <div className="meal-name">Déjeuner</div>
                </button>
                <button
                  className={`meal-card ${selectedMealType === 'dinner' ? 'selected' : ''}`}
                  onClick={() => setSelectedMealType('dinner')}
                >
                  <div className="meal-icon">🌙</div>
                  <div className="meal-name">Dîner</div>
                </button>
              </div>
            </div>

            {/* Bouton de validation */}
            <div className="planning-actions">
              <button
                className="btn-validate-planning"
                onClick={savePlanningToFirestore}
                disabled={!selectedDay || !selectedMealType || savingPlanning}
              >
                {savingPlanning ? 'Ajout en cours...' : 'Ajouter au planning'}
              </button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default RecipesList;
